export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'markaz-theme'

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system'
}

export function getThemeStorageKey() {
  return STORAGE_KEY
}

export function getStoredTheme(): ThemeMode {
  try {
    const storedTheme = window.localStorage.getItem(getThemeStorageKey())
    return isThemeMode(storedTheme) ? storedTheme : 'system'
  } catch {
    return 'system'
  }
}

export function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function resolveTheme(theme: ThemeMode): ResolvedTheme {
  return theme === 'system' ? getSystemTheme() : theme
}

export function getInitialThemeMode(): ThemeMode {
  const rootThemeMode = document.documentElement.dataset.themeMode
  return isThemeMode(rootThemeMode) ? rootThemeMode : getStoredTheme()
}

export function getInitialResolvedTheme(): ResolvedTheme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

export function applyTheme(theme: ThemeMode): ResolvedTheme {
  const resolvedTheme = resolveTheme(theme)
  const root = document.documentElement

  root.classList.toggle('dark', resolvedTheme === 'dark')
  root.dataset.theme = resolvedTheme
  root.dataset.themeMode = theme
  root.style.colorScheme = resolvedTheme

  try {
    window.localStorage.setItem(getThemeStorageKey(), theme)
  } catch {
    // Ignore storage failures and keep the current document state.
  }

  return resolvedTheme
}
